import { ESRI, GIS } from '@rsos/maps/constants/reverseGeocodeServices';
import gisReverseGeocodeLocation from './gisReverseGeocodeLocation';
import googleReverseGeocodeLocation from './googleReverseGeocodeLocation';

/**
 * Esri world geocode enhanced service url is used when a premium authority does not have a local
 * gis geocode service configured.
 * https://developers.arcgis.com/documentation/mapping-apis-and-services/service-endpoints/#geocoding-service-1
 */
export const ESRI_GEOCODE_SERVICE_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/';

/**
 * Parse the Esri address candidate and return the formatted address and addressObject. If the
 * address type is a Point of Interest (poi) such as businesses, landmarks, or geographic features,
 * use the address long label so that the Point of Interest name is included in the reverse geocode
 * address (rgAddress) field.
 * https://developers.arcgis.com/rest/geocode/api-reference/geocoding-service-output.htm
 * @param {Object} addressCandidate - The address attributes returned from GIS or Esri reverse
 * geocode service.
 * @param {String} rgAddressProvider - The reverse geocoding service address provider.
 */
export const parseGISAddressCandidate = (
  addressCandidate,
  rgAddressProvider,
) => {
  const address =
    addressCandidate?.['Addr_type']?.toLowerCase() === 'poi'
      ? addressCandidate?.['LongLabel']
      : addressCandidate?.['Match_addr']
        ? addressCandidate['Match_addr']
        : '';

  const addressObject = {
    home: addressCandidate?.['Address'] || '',
    street: addressCandidate?.['Street'] || '',
    city: addressCandidate?.['City'] || '',
    region: addressCandidate?.['Region'] || '',
    postal_code: addressCandidate?.['Postal'] || '',
    country: addressCandidate?.['CntryName'] || '',
  };

  return { address, addressObject, rgAddressProvider };
};

/**
 * Parse the Google address components and return a formatted address object.
 * @param {object} addressComponents - The address components returned from Google reverse geocode
 * service.
 */
export const parseGoogleAddressComponents = addressComponents => {
  const ShouldBeComponent = {
    home: ['street_number'],
    street: ['street_address', 'route'],
    city: [
      'locality',
      'sublocality',
      'sublocality_level_1',
      'sublocality_level_2',
      'sublocality_level_3',
      'sublocality_level_4',
    ],
    region: [
      'administrative_area_level_1',
      'administrative_area_level_2',
      'administrative_area_level_3',
      'administrative_area_level_4',
      'administrative_area_level_5',
    ],
    postal_code: ['postal_code'],
    country: ['country'],
  };

  let address = {
    home: '',
    street: '',
    city: '',
    region: '',
    postal_code: '',
    country: '',
  };

  if (addressComponents) {
    addressComponents.forEach(component => {
      for (var shouldBe in ShouldBeComponent) {
        if (ShouldBeComponent[shouldBe].indexOf(component.types[0]) !== -1) {
          if (shouldBe === 'country') {
            address[shouldBe] = component.short_name;
          } else {
            address[shouldBe] = component.long_name;
          }
        }
      }
    });
  }

  return address;
};

/**
 * Reverse geocode coordinates to an address. Returns a formatted address and addressObject. The
 * reverse geocode service priority should be GIS, then Esri, then Google depending on if an address
 * is found.
 * @param {Number} latitude - The location's latitude
 * @param {Number} longitude - The location's longitude
 * @param {Function} onReverseGeocoding - Sets if the location is being reverse geocoded
 * @param {String} service - The reverse geocode service to use
 * @param {String} serviceUrl - The GIS or ESRI geocode service url
 * @param {String} token - The GIS authentication token or apiKey
 */
export const reverseGeocodeLocationToAddress = async ({
  latitude,
  longitude,
  onReverseGeocoding,
  service,
  serviceUrl,
  token,
}) => {
  switch (service) {
    case GIS: {
      try {
        if (!serviceUrl || !token)
          throw new Error('Missing GIS service url or token.');

        const addressCandidate = await gisReverseGeocodeLocation({
          latitude,
          longitude,
          onReverseGeocoding,
          serviceUrl,
          token,
        });

        return addressCandidate
          ? parseGISAddressCandidate(addressCandidate, 'GIS')
          : null;
      } catch (error) {
        return reverseGeocodeLocationToAddress({
          latitude,
          longitude,
          onReverseGeocoding,
          service: ESRI,
          serviceUrl: ESRI_GEOCODE_SERVICE_URL,
        });
      }
    }
    case ESRI: {
      try {
        const addressCandidate = await gisReverseGeocodeLocation({
          latitude,
          longitude,
          onReverseGeocoding,
          serviceUrl: ESRI_GEOCODE_SERVICE_URL,
        });

        return addressCandidate
          ? parseGISAddressCandidate(addressCandidate, 'Esri')
          : null;
      } catch (error) {
        return reverseGeocodeLocationToAddress({
          latitude,
          longitude,
          onReverseGeocoding,
        });
      }
    }
    default: {
      try {
        const { address, addressObject } = await googleReverseGeocodeLocation({
          latitude,
          longitude,
          onReverseGeocoding,
        });

        let formattedAddressObject = null;

        if (addressObject)
          formattedAddressObject = parseGoogleAddressComponents(addressObject);

        return {
          address,
          addressObject: formattedAddressObject,
          rgAddressProvider: 'Google',
        };
      } catch (error) {
        return null;
      }
    }
  }
};
